import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import type { useCollection } from "./useCollection";

type Collection = ReturnType<typeof useCollection>;

function conciseFilterOptions(
  options: { value: string; label: string }[],
  clearValue = "",
) {
  return options.map((option) =>
    option.value === clearValue && /^All(?:\s|$)/i.test(option.label)
      ? { ...option, label: "All" }
      : option,
  );
}

export function CollectionToolbar({
  collection: c,
  label,
  extraFilters = [],
  filters,
  filterLabel = "Status",
  searchPlaceholder,
  extraFiltersAfter = false,
  className,
}: {
  collection: Collection;
  label: string;
  extraFilters?: {
    label: string;
    ariaLabel: string;
    value: string;
    clearValue: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
  }[];
  filters?: { value: string; label: string }[];
  filterLabel?: string;
  searchPlaceholder?: string;
  extraFiltersAfter?: boolean;
  className?: string;
}) {
  if (!c.controls) return null;

  return (
    <div className={cn("collection-toolbar", className)}>
      <label className="collection-search">
        <Search aria-hidden className="collection-search-icon" />
        <span>Quick search</span>
        <input
          aria-label={`Search ${label}`}
          type="search"
          className="w-full !pl-10"
          placeholder={searchPlaceholder ?? `Search ${label}`}
          value={c.query}
          onChange={(e) => c.setQuery(e.target.value)}
        />
      </label>
      {!extraFiltersAfter &&
        extraFilters.map((filter) => (
          <label className="collection-filter" key={filter.ariaLabel}>
            <span>{filter.label}</span>
            <TkticSelect
              ariaLabel={filter.ariaLabel}
              options={conciseFilterOptions(filter.options, filter.clearValue)}
              value={filter.value}
              onChange={filter.onChange}
            />
          </label>
        ))}
      {filters ? (
        <label className="collection-filter">
          <span>{filterLabel}</span>
          <TkticSelect
            ariaLabel={`Filter ${label}`}
            options={conciseFilterOptions(filters)}
            value={c.filter}
            onChange={c.setFilter}
          />
        </label>
      ) : null}
      {extraFiltersAfter &&
        extraFilters.map((filter) => (
          <label className="collection-filter" key={filter.ariaLabel}>
            <span>{filter.label}</span>
            <TkticSelect
              ariaLabel={filter.ariaLabel}
              options={conciseFilterOptions(filter.options, filter.clearValue)}
              value={filter.value}
              onChange={filter.onChange}
            />
          </label>
        ))}
      <Button
        disabled={
          !c.query &&
          !c.filter &&
          extraFilters.every((filter) => filter.value === filter.clearValue)
        }
        variant="ghost"
        onClick={() => {
          c.clear();
          for (const filter of extraFilters) {
            filter.onChange(filter.clearValue);
          }
        }}
      >
        Clear filters
      </Button>
    </div>
  );
}
export function CollectionPagination({
  collection: c,
  label = "results",
}: {
  collection: Collection;
  label?: string;
}) {
  if (!c.controls) return null;
  return (
    <div className="collection-pagination">
      <span>
        {c.filteredTotal
          ? `${(c.page - 1) * c.pageSize + 1}–${Math.min(c.page * c.pageSize, c.filteredTotal)} of ${c.filteredTotal} ${label}`
          : `0 of ${c.total} ${label}`}
      </span>
      <div className="flex items-center gap-2">
        <span>{"Per page"}</span>
        <TkticSelect
          ariaLabel="Rows per page"
          value={String(c.pageSize)}
          options={[5, 10, 25, 50].map((v) => ({
            value: String(v),
            label: String(v),
          }))}
          onChange={(v) => c.setPageSize(Number(v))}
        />
        <PageNavigation page={c.page} pages={c.pages} onChange={c.setPage} />
      </div>
    </div>
  );
}

export function PageNavigation({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (page: number) => void;
}) {
  const visible = [...new Set([1, page - 1, page, page + 1, pages])]
    .filter((n) => n > 0 && n <= pages)
    .sort((a, b) => a - b);
  return (
    <nav className="collection-pages" aria-label="Pagination">
      <Button
        size="icon"
        variant="ghost"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft />
      </Button>
      {visible.flatMap((n, index) => [
        index > 0 && n - visible[index - 1] > 1 ? (
          <span key={`gap-${n}`} aria-hidden>
            …
          </span>
        ) : null,
        <Button
          key={n}
          variant="ghost"
          aria-label={`Page ${n}`}
          aria-current={n === page ? "page" : undefined}
          onClick={() => onChange(n)}
        >
          {n}
        </Button>,
      ])}
      <Button
        size="icon"
        variant="ghost"
        aria-label="Next page"
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight />
      </Button>
    </nav>
  );
}
